// UnsavedChangesConfirmModal — generic warning dialog rendered by
// `UnsavedChangesProvider` when a dirty editable surface attempts to close.
//
// @aiPurpose Render the shared two-action unsaved-changes prompt.
// @aiInvariant Two one-step actions only: safe primary "Continue editing"
//   first with initial focus, destructive "Close without saving" second,
//   rendered as a vertical full-width stack inside the modal body (not in
//   `Modal.footer`) so long Russian labels never overflow narrow drawer
//   widths. Escape and backdrop take the safe branch via the underlying
//   Modal's `onClose`.
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './Button.js';
import { Modal } from './Modal.js';

export interface UnsavedChangesConfirmModalProps {
  open: boolean;
  onContinue: () => void;
  onDiscard: () => void;
}

export function UnsavedChangesConfirmModal({
  open,
  onContinue,
  onDiscard,
}: UnsavedChangesConfirmModalProps) {
  const { t } = useTranslation('common');
  const continueRef = useRef<HTMLButtonElement | null>(null);
  return (
    <Modal
      open={open}
      onClose={onContinue}
      title={t('unsavedChanges.title')}
      width={420}
      initialFocusRef={continueRef}
    >
      <div data-testid="unsaved-changes-modal" style={{ fontSize: 13, color: 'var(--fg-2)' }}>
        {t('unsavedChanges.body')}
      </div>
      {/* Vertical full-width action stack lives in the modal body, not in
          Modal.footer, so the long Russian labels never overflow on narrow
          drawer widths. Mirrors the proven DraftCloseConfirmModal pattern. */}
      <div
        data-testid="unsaved-changes-actions"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          marginTop: 16,
        }}
      >
        <Button
          kind="primary"
          onClick={onContinue}
          ref={continueRef}
          data-testid="unsaved-changes-continue"
          style={{ width: '100%', justifyContent: 'center', whiteSpace: 'normal' }}
        >
          {t('unsavedChanges.continueEditing')}
        </Button>
        <Button
          kind="danger"
          onClick={onDiscard}
          data-testid="unsaved-changes-discard"
          style={{ width: '100%', justifyContent: 'center', whiteSpace: 'normal' }}
        >
          {t('unsavedChanges.closeWithoutSaving')}
        </Button>
      </div>
    </Modal>
  );
}
